import { Link } from "react-router-dom";
import { Instagram, Music2, Pin, Facebook, Youtube, Linkedin } from "lucide-react";
import { Logo, Wordmark } from "./Logo";
import { LangSwitcher, scrollToSection } from "./Navbar";
import { useLang } from "@/i18n/LanguageContext";
import { getFeatureLinks } from "@/data/featurePages";
import { SOCIAL_LINKS } from "@/data/socialLinks";

const XIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const GUIDE_LABELS = {
  de: { women: "Stil-Guide für Damen", men: "Stil-Guide für Männer" },
  en: { women: "Style Guide for Women", men: "Style Guide for Men" },
  es: { women: "Guía de estilo para mujeres", men: "Guía de estilo para hombres" },
  it: { women: "Guida di stile per donna", men: "Guida di stile per uomo" },
  fr: { women: "Guide de style pour femmes", men: "Guide de style pour hommes" },
};

export default function Footer() {
  const { t, lang } = useLang();
  const guides = GUIDE_LABELS[lang] || GUIDE_LABELS.de;
  return (
    <footer className="border-t" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Logo size={40} />
              <Wordmark className="text-base" />
            </div>
            <p className="mt-4 text-sm" style={{ color: "var(--muted-warm)" }}>
              ✦ {t.footer.claim}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {[
                { Icon: Instagram, label: "Instagram", href: SOCIAL_LINKS.instagram },
                { Icon: Music2, label: "TikTok", href: SOCIAL_LINKS.tiktok },
                { Icon: Pin, label: "Pinterest", href: SOCIAL_LINKS.pinterest },
                { Icon: Facebook, label: "Facebook", href: SOCIAL_LINKS.facebook },
                { Icon: Youtube, label: "YouTube", href: SOCIAL_LINKS.youtube },
                { Icon: XIcon, label: "X", href: SOCIAL_LINKS.x },
                { Icon: Linkedin, label: "LinkedIn", href: SOCIAL_LINKS.linkedin },
              ].filter(({ href }) => href).map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-300 hover:border-[var(--camel)] hover:text-[var(--camel)]"
                  style={{ borderColor: "var(--line)" }}
                >
                  <Icon size={17} strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 text-sm">
            <span className="text-xs uppercase" style={{ color: "var(--muted-warm)", letterSpacing: "0.14em" }}>Smart Mirror</span>
            {[
              ["smart-mirror", lang === "de" ? "Smart Mirror Guide" : "Smart Mirror Guide"],
              ["smart-mirror-kosten", lang === "de" ? "Kosten & Preise" : "Costs & Prices"],
              ["smart-mirror-boutique", lang === "de" ? "Für Boutiquen" : "For Boutiques"],
              ["smart-mirror-anbieter", lang === "de" ? "Anbieter-Vergleich" : "Provider Comparison"],
              ["virtuelle-anprobe-spiegel", lang === "de" ? "Virtuelle Anprobe" : "Virtual Try-On"],
              ["glossar", lang === "de" ? "Glossar" : "Glossary"],
            ].map(([slug, label]) => (
              <Link key={slug} to={`/${slug}`} data-testid={`footer-seo-${slug}`} className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
                {label}
              </Link>
            ))}
          </div>

          <div className="flex flex-col gap-3 text-sm">
            <span className="text-xs uppercase" style={{ color: "var(--muted-warm)", letterSpacing: "0.14em" }}>{t.nav.features}</span>
            {getFeatureLinks(lang).map((f) => (
              <Link key={f.slug} to={`/features/${f.slug}`} data-testid={`footer-feature-${f.slug}`} className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
                {f.kicker}
              </Link>
            ))}
          </div>

          <div className="flex flex-col gap-3 text-sm">
            <Link to="/impressum" data-testid="footer-link-impressum" className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
              {t.footer.impressum}
            </Link>
            <Link to="/datenschutz" data-testid="footer-link-datenschutz" className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
              {t.footer.datenschutz}
            </Link>
            <Link to="/kontakt" data-testid="footer-link-kontakt" className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
              {t.footer.kontakt}
            </Link>
            <Link to="/support" data-testid="footer-link-support" className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
              Support
            </Link>
            <Link to="/business" data-testid="footer-link-casestudy" className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
              {t.caseStudy.kicker} ✦
            </Link>
            <Link to="/presse" data-testid="footer-link-presse" className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
              {t.footer.presse}
            </Link>
            <Link to="/blog" data-testid="footer-link-blog" className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
              {t.nav.blog}
            </Link>
            <Link to="/stil-guide-damen" data-testid="footer-link-guide-damen" className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
              {guides.women}
            </Link>
            <Link to="/stil-guide-maenner" data-testid="footer-link-guide-maenner" className="gold-underline w-fit transition-colors duration-300 hover:text-[var(--camel)]">
              {guides.men}
            </Link>
          </div>

          <div className="flex flex-col items-start gap-3">
            <span className="text-xs uppercase" style={{ color: "var(--muted-warm)", letterSpacing: "0.14em" }}>
              {t.footer.language}
            </span>
            <LangSwitcher up align="left" />
          </div>
        </div>

        <div className="mt-12 border-t pt-6 text-center text-xs" style={{ borderColor: "var(--line)", color: "var(--muted-warm)" }} data-testid="footer-rights">
          {t.footer.rights}
        </div>
      </div>
    </footer>
  );
}
